import React, { useState, useEffect } from 'react';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

interface Permissions {
  [key: string]: boolean;
}

interface RolePermission {
  role: string;
  permissions: Permissions;
}

const RolePermissions: React.FC = () => {
  const { hasPermission } = useAuth();
  const [permissionsData, setPermissionsData] = useState<RolePermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Permission keys (adjust if necessary based on your rolePermissions.json)
  const canViewPermissions = hasPermission('settings.user_management');
  const canEditPermissions = hasPermission('settings.user_management');

  // Fetch current permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get('/settings/permissions');
        setPermissionsData(response.data);
      } catch (err) {
        console.error("Error fetching permissions:", err);
        setError("Не удалось загрузить разрешения.");
      } finally {
        setLoading(false);
      }
    };

    if (canViewPermissions) {
      fetchPermissions();
    } else {
      setError("У вас нет прав для просмотра этой страницы.");
      setLoading(false);
    }
  }, [canViewPermissions]);

  // Handle checkbox change
  const handlePermissionChange = (roleIndex: number, permissionKey: string, value: boolean) => {
    setPermissionsData(prevData => {
      const newData = [...prevData];
      newData[roleIndex].permissions[permissionKey] = value;
      return newData;
    });
    setSuccess(null); // Clear success message on change
  };

  // Handle saving changes
  const handleSaveChanges = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await axios.put('/settings/permissions', permissionsData);
      setSuccess("Разрешения успешно сохранены.");
      // Optionally: Reload permissions from backend to confirm changes
      // const response = await axios.get('/settings/permissions');
      // setPermissionsData(response.data);
    } catch (err) {
      console.error("Error saving permissions:", err);
      setError("Ошибка при сохранении разрешений.");
    } finally {
      setSaving(false);
    }
  };

  // Group permissions by section (e.g., 'clients', 'orders') for better display
  const getGroupedPermissions = (): Record<string, string[]> => {
    if (permissionsData.length === 0) return {};
    const allKeys = Object.keys(permissionsData[0].permissions);
    const grouped: Record<string, string[]> = {};
    allKeys.forEach(key => {
      const section = key.split('.')[0];
      if (!grouped[section]) {
        grouped[section] = [];
      }
      grouped[section].push(key);
    });
    // Sort sections and permissions within sections
    Object.keys(grouped).forEach(section => grouped[section].sort());
    return Object.keys(grouped).sort().reduce((acc, key) => {
      acc[key] = grouped[key];
      return acc;
    }, {} as Record<string, string[]>);
  };

  const groupedPermissions = getGroupedPermissions();

  if (loading) return <LoadingSpinner />;
  if (error && !canViewPermissions) {
    return <div className="p-6 bg-red-100 text-red-700 rounded-md">{error}</div>;
  }

  // Optional: Add a specific message if user can view but not edit
  if (!canEditPermissions) {
    // You might want to show the table as read-only or display a message
    // For simplicity, we'll just disable the save button later
  }

  return (
    <div>
      <PageHeader title="Управление правами ролей" backLink="/settings" />

      <div className="bg-white rounded-lg shadow p-6">
        {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}
        {success && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">{success}</div>}

        {/* Optional: Message if user cannot edit */}
        {!canEditPermissions && (
          <div className="mb-4 p-3 bg-yellow-100 text-yellow-700 rounded">
            У вас нет прав на редактирование разрешений.
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider border-r border-gray-200">
                  Разрешение
                </th>
                {permissionsData.map(rolePerm => (
                  <th
                    key={rolePerm.role}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider border-r border-gray-200"
                  >
                    {rolePerm.role}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Object.entries(groupedPermissions).map(([section, keys]) => (
                <React.Fragment key={section}>
                  {/* Section Header Row */}
                  <tr className="bg-gray-50">
                    <td colSpan={permissionsData.length + 1} className="px-4 py-2 text-sm font-semibold text-gray-700">
                      {section.charAt(0).toUpperCase() + section.slice(1)} {/* Capitalize section */}
                    </td>
                  </tr>
                  {/* Permission Rows */}
                  {keys.map(permissionKey => (
                    <tr key={permissionKey}>
                      <td className="px-4 py-2 text-sm text-gray-700 border-r border-gray-200">
                        {permissionKey}
                      </td>
                      {permissionsData.map((rolePerm, roleIndex) => (
                        <td key={`${rolePerm.role}-${permissionKey}`} className="px-4 py-2 text-center border-r border-gray-200">
                          <input
                            type="checkbox"
                            className={`h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded ${!canEditPermissions ? 'cursor-not-allowed opacity-50' : ''
                              }`}
                            checked={rolePerm.permissions[permissionKey] || false}
                            onChange={(e) => handlePermissionChange(roleIndex, permissionKey, e.target.checked)}
                            disabled={!canEditPermissions}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSaveChanges}
            disabled={saving || !canEditPermissions}
            className={`btn-primary ${!canEditPermissions ? 'opacity-50 cursor-not-allowed' : ''
              }`}
          >
            {saving ? 'Сохранение...' : 'Сохранить изменения'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RolePermissions;
